import * as React from "react"
import { createPortal } from "react-dom"
import { X } from "lucide-react"
import { cn } from "../../lib/utils"
import { Button } from "./button"

interface DialogProps {
  isOpen: boolean
  onClose: () => void
  title: React.ReactNode
  description?: React.ReactNode
  /** Icon shown in a tile beside the title. */
  icon?: React.ReactNode
  /** Sticky action row at the bottom of the panel. */
  footer?: React.ReactNode
  size?: "sm" | "md" | "lg"
  children: React.ReactNode
}

const sizes = {
  sm: "sm:max-w-sm",
  md: "sm:max-w-md",
  lg: "sm:max-w-lg",
}

/**
 * Modal dialog rendered in a portal. Becomes a bottom sheet on narrow screens.
 * Closes on Escape and backdrop click, locks body scroll and restores focus on close.
 */
function Dialog({ isOpen, onClose, title, description, icon, footer, size = "md", children }: DialogProps) {
  const panelRef = React.useRef<HTMLDivElement>(null)
  const titleId = React.useId()
  const descriptionId = React.useId()

  // Callers usually pass an inline onClose; keep the latest in a ref so the
  // open/close effect below doesn't re-run (and steal focus) on every render.
  const onCloseRef = React.useRef(onClose)
  onCloseRef.current = onClose

  React.useEffect(() => {
    if (!isOpen) return

    const panel = panelRef.current
    const focusAlreadyInside = !!panel && panel.contains(document.activeElement)
    const previouslyFocused = focusAlreadyInside ? null : (document.activeElement as HTMLElement | null)
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCloseRef.current()
    }

    document.addEventListener("keydown", handleKeyDown)
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = "hidden"
    // Respect autoFocus on a field inside the dialog; otherwise focus the panel itself.
    if (!focusAlreadyInside) panel?.focus()

    return () => {
      document.removeEventListener("keydown", handleKeyDown)
      document.body.style.overflow = previousOverflow
      previouslyFocused?.focus?.()
    }
  }, [isOpen])

  if (!isOpen) return null

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center sm:p-4">
      <div
        className="absolute inset-0 animate-fade-in bg-black/60 backdrop-blur-[2px]"
        onClick={onClose}
        aria-hidden
      />

      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descriptionId : undefined}
        tabIndex={-1}
        className={cn(
          "relative flex max-h-[92vh] w-full flex-col overflow-hidden outline-none",
          "rounded-t-2xl border border-line-strong bg-surface shadow-pop sm:rounded-2xl",
          "animate-pop-in",
          sizes[size]
        )}
      >
        <header className="flex items-start gap-3 px-5 pt-5 pb-4">
          {icon && (
            <span className="grid size-9 shrink-0 place-items-center rounded-xl border border-line bg-raised text-brand [&_svg]:size-[18px]">
              {icon}
            </span>
          )}
          <div className="min-w-0 flex-1">
            <h2 id={titleId} className="text-base font-semibold tracking-tight text-fg">
              {title}
            </h2>
            {description && (
              <p id={descriptionId} className="mt-0.5 text-[13px] text-fg-subtle">
                {description}
              </p>
            )}
          </div>
          <Button variant="ghost" size="icon-sm" className="-mt-1 -mr-1.5" onClick={onClose} aria-label="Close dialog">
            <X />
          </Button>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 pb-5">{children}</div>

        {footer && (
          <footer className="flex items-center gap-2 border-t border-line bg-canvas/40 px-5 py-3.5">
            {footer}
          </footer>
        )}
      </div>
    </div>,
    document.body
  )
}

export { Dialog }
export type { DialogProps }
